/**
 * i18n Client Configuration
 * Client-side i18n setup using react-i18next.
 *
 * Hydrate-first: SSR passes resources via hydrateClientI18n (Task 4 provider).
 * HttpBackend is only used as a fallback when hydrate did not run (e.g. CSR-only).
 */

'use client';

import { useCallback } from 'react';
import i18next, { type TFunction, type i18n as I18nInstance } from 'i18next';
import { initReactI18next, useTranslation as useTranslationBase } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { i18nConfig, Namespace, defaultNamespace, fallbackLngMap } from './config';
import type { Locale } from './config';
import type { I18nResources } from './load-resources';
import { localeHttpBackendLoadPath } from './load-resources';
import { translate, type TupleTFunction } from './translation-key';

export type { TranslationKey, TupleTFunction } from './translation-key';

let hydrated = false;
let i18nReadyResolve: ((t: TFunction) => void) | undefined;
/** Resolves after StorefrontI18nProvider hydrate or HttpBackend fallback init. */
const i18nReady: Promise<TFunction> = new Promise((resolve) => {
  i18nReadyResolve = resolve;
});

function applyResourceBundles(resources: I18nResources): void {
  for (const [lng, nsMap] of Object.entries(resources)) {
    for (const [ns, bundle] of Object.entries(nsMap)) {
      i18next.addResourceBundle(lng, ns, bundle, true, true);
    }
  }
}

export function hydrateClientI18n(locale: Locale, resources: I18nResources): I18nInstance {
  if (!hydrated) {
    i18next.use(initReactI18next).init({
      lng: locale,
      fallbackLng: fallbackLngMap,
      supportedLngs: [...i18nConfig.locales],
      defaultNS: defaultNamespace,
      ns: [...i18nConfig.namespaces],
      resources,
      returnEmptyString: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      initImmediate: false,
    });
    hydrated = true;
    i18nReadyResolve?.(i18next.t.bind(i18next));
  } else {
    applyResourceBundles(resources);
    if (i18next.language !== locale) {
      i18next.changeLanguage(locale);
    }
  }
  return i18next as I18nInstance;
}

export function ensureHttpBackendFallback(locale: Locale): Promise<TFunction> {
  if (hydrated) {
    return i18next.changeLanguage(locale);
  }
  return i18next
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: fallbackLngMap,
      supportedLngs: [...i18nConfig.locales],
      defaultNS: defaultNamespace,
      ns: [...i18nConfig.namespaces],
      backend: {
        loadPath: localeHttpBackendLoadPath(),
      },
      returnEmptyString: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    })
    .then((t) => {
      hydrated = true;
      i18nReadyResolve?.(t);
      return t;
    });
}

export { i18nReady };

export async function changeLanguage(locale: Locale): Promise<TFunction> {
  if (!hydrated) {
    return ensureHttpBackendFallback(locale);
  }
  if (i18next.language === locale) {
    return i18next.t.bind(i18next);
  }
  if (!i18next.hasResourceBundle(locale, defaultNamespace)) {
    const { loadI18nResources } = await import('./load-resources');
    applyResourceBundles(await loadI18nResources(locale));
  }
  return i18next.changeLanguage(locale);
}

export function useTranslation(ns: Namespace = defaultNamespace): {
  t: TupleTFunction;
  i18n: ReturnType<typeof useTranslationBase>['i18n'];
  ready: boolean;
} {
  const { t: baseT, i18n, ready } = useTranslationBase(ns);
  const t: TupleTFunction = useCallback(
    (key, options) => translate(baseT, key, options),
    [baseT],
  );
  return { t, i18n, ready };
}

export function getCurrentLanguage(): string {
  return i18next.language || i18nConfig.defaultLocale;
}

export function resetI18n(): void {
  hydrated = false;
}
